/* eslint-disable complexity */
import React from "react";
import { Button, Stack, Alert, CircularProgress, Box } from "@mui/material";
import { FormProvider, useForm, useFormState, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import moment from "moment";
import { omitBy, isEqual } from "lodash";

import { ModalProps, createUseModal, useModal } from "@imminently/react/context/modals";
import { useNotify } from "@imminently/react/store/notifications";
import {
  Dialog,
  DialogTitle,
  DialogContent
} from "@imminently/react/ui/core/dialog";
import { Text } from "@imminently/react/ui/core/text";

import { GenericFormRenderer } from "./formRenderer";
import { getFormConfig, NMHS_AMBULANCE_MODE_OF_ARRIVAL_CODE } from "./configurations";
import { ActingAsBanner } from "@/components/ActingAsBanner";
import { css, Theme } from "@/theme";
import { encounterService } from "@/services";
import { formatName } from "@/global";
import global from "@/global";
import { Patient, PatientEncounter, InfectiousSymptom, Task, TaskType } from "@/resources/types";
import { medicareSchemaProps } from "../addPatient/medicareValidate";
import { createEmailSchemaWithDuplicateCheck } from "../addPatient/emailOrEmptyStringSchema";
import { buildPatientPayload, buildEncounterSpecificPayload } from "../addPatient/addPatientContext";
import { useAddObservationModal } from "../addObservation";
import { useSettingsContext } from "@/pages/settings/SettingsProvider";
import { useSensitiveActionAuthorization } from "@/components/SensitiveActionAuthorization";
import { generateMevSummarySectionPdf } from "@/util/printable-obschart";
import { mevSummaryAutoOpenConfig } from "@/util/printable-obschart/config/chartdef";
import {
  shouldAutoOpenDeferredMevSummary,
  shouldAutoOpenMevSummaryAfterSave,
} from "./mevSummaryAutoOpen";
import {
  activateMevSummaryDeferredFlow,
  canAttemptDeferredMevOpen,
  consumeDeferredMevOpen,
  initialMevSummaryDeferredState,
  markDeferredObservationSaved,
  type MevSummaryDeferredState,
} from "./mevSummaryDeferredState";
import { patientToGenericFormValues } from "./patientFormValues";
import { ExternalRegisterSecureDetailsModal } from "@/components/externalregister/ExternalRegisterSecureDetailsModal";
import { useExternalRegisterSecureDetailsModalState } from "@/components/externalregister/useExternalRegisterSecureDetailsModalState";
import {
  canOpenExternalRegisterDetails,
  canRenderExternalRegisterClinicalIndicator,
} from "@/util/externalRegister";
import { PatientStatusWidgetRuntimeProvider } from "./patientStatusWidgetContext";

const NMHS_INJURY_COMPLAINT_ROOT_CODE = "J0000";

const complaintCodeTreeContains = (node: any, complaintCode: string) => {
  return node.id === complaintCode || node.children?.some((child: any) => complaintCodeTreeContains(child, complaintCode)) === true;
};

const isInjuryComplaintCode = (complaintCodes: any[], complaintCode: string) => {
  const injuryRoot = complaintCodes.find(node => node.id === NMHS_INJURY_COMPLAINT_ROOT_CODE);
  return !!complaintCode && !!injuryRoot && complaintCodeTreeContains(injuryRoot, complaintCode);
};

// Validation schemas (copied from addPatientContext)
const optional = (schema: yup.AnySchema) =>
  yup.lazy((v) => (v === "" ? yup.string() : schema));

const dateOfBirthSchema = () => yup.date()
  .transform((value, originalValue) => moment.isMoment(originalValue) ? originalValue.toDate() : value)
  .typeError("Enter a valid date of birth")
  .min(moment().subtract(120, "years").startOf("day").toDate(), "Date of birth cannot be more than 120 years ago")
  .max(moment().endOf("day").toDate(), "Date of birth cannot be in the future");

// Helper function for all-or-none validation: checks if all fields are filled or all are blank
const allOrNone = (fields: (string | undefined)[]): boolean => {
  const trimmedFields = fields.map(f => (f || "").trim());
  const anyFilled = trimmedFields.some(f => f !== "");
  const allFilled = trimmedFields.every(f => f !== "");
  return !anyFilled || allFilled;
};

// Helper function for next of kin validation: all fields must be filled if any is filled
const validateNextOfKin = (firstName: any, lastName: any, relationship: any) => {
  return allOrNone([firstName, lastName, relationship]);
};

// Helper function for GP Details validation: all 3 fields must be filled together or all blank
const validateGPDetails = (clinicName: any, doctorFirstName: any, doctorLastName: any) => {
  return allOrNone([clinicName, doctorFirstName, doctorLastName]);
};

// Helper function for previous name entry validation: both firstName and lastName must be filled together or both blank
const validatePreviousNameEntry = (firstName: any, lastName: any) => {
  return allOrNone([firstName, lastName]);
};

const baseSchema = {
  submissionType: yup.mixed(),
  dob: yup.mixed().when("submissionType", ([submissionType]) => {
    return submissionType !== "unknown"
      ? dateOfBirthSchema().required("Date of birth is required")
      : optional(dateOfBirthSchema());
  }),
  sex: yup.string().trim().when("submissionType", ([submissionType], schem) => {
    return submissionType !== "unknown"
      ? schem.required("Sex at birth is required")
      : schem;
  }),
  gender: optional(yup.mixed()),
  infectiousSymptoms: yup
    .array()
    .of(yup.mixed().oneOf(Object.values(InfectiousSymptom)))
    .optional(),
  ...medicareSchemaProps,
  umrn: yup.string().trim(),
  // GP Details - all optional
  gpClinicName: yup.string().trim().optional(),
  gpDoctorFirstName: yup.string().trim().optional(),
  gpDoctorLastName: yup.string().trim().optional(),
  problem: yup.string().trim().required("Problem is required"),
  complaintCode: yup.string().trim().required("Complaint code is required"),
  atsScore: yup.string().trim().required("ATS score is required"),
  typeOfVisit: yup.string().trim().required("Type of visit is required"),
  modeOfArrival: yup.string().trim().required("Mode of arrival is required"),
  ambulanceCaseNumber: yup.string().trim().when("modeOfArrival", ([modeOfArrival], schem) => {
    return modeOfArrival === NMHS_AMBULANCE_MODE_OF_ARRIVAL_CODE
      ? schem.required("Ambulance case number is required")
      : schem;
  }),
  location: yup.string().trim().required("Location is required"),
  stream: yup.string().trim().required("Stream is required"),
  injury: yup.boolean(),
  cause: yup.string().trim().when("injury", ([injury], schem) => {
    return injury ? schem.required("Cause is required") : schem;
  }),
  intent: yup.string().trim().when("injury", ([injury], schem) => {
    return injury ? schem.required("Intent is required") : schem;
  }),
  // Previous names - optional array, each entry must have both firstName and lastName filled together or both blank
  previousNames: yup.array().of(yup.object().shape({
    firstName: yup.string().test("prev-name-all-or-none", "Please fill both previous name fields or leave them both blank", function(value) {
      const { lastName } = this.parent;
      return validatePreviousNameEntry(value, lastName);
    }),
    lastName: yup.string().test("prev-name-all-or-none", "Please fill both previous name fields or leave them both blank", function(value) {
      const { firstName } = this.parent;
      return validatePreviousNameEntry(firstName, value);
    }),
  })).optional(),
};

// Concierge schema - firstName, lastName, presentingComplaint required; dob, phone, and email are optional
const createConciergeSchema = yup.object().shape({
  ...baseSchema,
  firstName: yup.string().trim().ensure().required("First name is required"),
  lastName: yup.string().trim().ensure().required("Last name is required"),
  dob: optional(dateOfBirthSchema()),
  sex: yup.string().trim().optional(),
  patientComplaint: yup.string().trim().required("Patient complaint is required"),
  problem: yup.string().trim().optional(), // Override baseSchema to make problem optional for concierge
  complaintCode: yup.string().trim().optional(), // Override baseSchema - not required for concierge
  atsScore: yup.string().trim().optional(), // Override baseSchema - not required for concierge
  typeOfVisit: yup.string().trim().optional(),
  modeOfArrival: yup.string().trim().optional(),
  ambulanceCaseNumber: yup.string().trim().optional(),
  location: yup.string().trim().optional(),
  stream: yup.string().trim().optional(),
  cause: yup.string().trim().optional(),
  intent: yup.string().trim().optional(),
  phone: yup.string().ensure().test("phone-format", "Invalid phone format", function(value) {
    // Only validate phone format if a value is provided
    if (!value || typeof value !== "string" || value.trim() === "") {
      return true; // Skip format validation if empty
    }
    return yup.string().phone().isValidSync(value);
  }),
  email: yup.string().ensure().test("email-format", "Invalid email format", function(value) {
    // Only validate email format if a value is provided
    if (!value || typeof value !== "string" || value.trim() === "") {
      return true; // Skip format validation if empty
    }
    return yup.string().email().isValidSync(value);
  }),
  // Next of kin - conditional validation: if any field is filled, all three must be filled
  nextOfKinFirstName: yup.string().trim().test("nok-all-or-none", "Please fill all next of kin fields or leave them all blank", function(value) {
    const { nextOfKinLastName, nextOfKinRelationship } = this.parent;
    return validateNextOfKin(value, nextOfKinLastName, nextOfKinRelationship);
  }),
  nextOfKinLastName: yup.string().trim().test("nok-all-or-none", "Please fill all next of kin fields or leave them all blank", function(value) {
    const { nextOfKinFirstName, nextOfKinRelationship } = this.parent;
    return validateNextOfKin(nextOfKinFirstName, value, nextOfKinRelationship);
  }),
  nextOfKinRelationship: yup.string().trim().test("nok-all-or-none", "Please fill all next of kin fields or leave them all blank", function(value) {
    const { nextOfKinFirstName, nextOfKinLastName } = this.parent;
    return validateNextOfKin(nextOfKinFirstName, nextOfKinLastName, value);
  }),
  // GP Details - conditional validation: if any field is filled, all three must be filled
  gpClinicName: yup.string().trim().test("gp-all-or-none", "Please fill all GP details fields or leave them all blank", function(value) {
    const { gpDoctorFirstName, gpDoctorLastName } = this.parent;
    return validateGPDetails(value, gpDoctorFirstName, gpDoctorLastName);
  }),
  gpDoctorFirstName: yup.string().trim().test("gp-all-or-none", "Please fill all GP details fields or leave them all blank", function(value) {
    const { gpClinicName, gpDoctorLastName } = this.parent;
    return validateGPDetails(gpClinicName, value, gpDoctorLastName);
  }),
  gpDoctorLastName: yup.string().trim().test("gp-all-or-none", "Please fill all GP details fields or leave them all blank", function(value) {
    const { gpClinicName, gpDoctorFirstName } = this.parent;
    return validateGPDetails(gpClinicName, gpDoctorFirstName, value);
  }),
});

const createSchema = yup.object().shape({
  ...baseSchema,
  firstName: yup.string().trim().ensure().when("submissionType", ([submissionType], schem) => {
    return submissionType !== "unknown"
      ? schem.required("First name is required")
      : schem;
  }),
  lastName: yup.string().trim().ensure().when("submissionType", ([submissionType], schem) => {
    return submissionType !== "unknown"
      ? schem.required("Last name is required")
      : schem;
  }),
  phone: optional(yup.string().phone()),
  email: createEmailSchemaWithDuplicateCheck(),
  // Delegate fields - optional
  hasDelegatePresent: yup.boolean(),
  delegateFirstName: yup.string().trim().when(["hasDelegatePresent", "delegateCanAct"], ([hasDelegatePresent, delegateCanAct], schem) => {
    return hasDelegatePresent && delegateCanAct
      ? schem.required("Delegate first name is required")
      : schem;
  }),
  delegateLastName: yup.string().trim().when(["hasDelegatePresent", "delegateCanAct"], ([hasDelegatePresent, delegateCanAct], schem) => {
    return hasDelegatePresent && delegateCanAct
      ? schem.required("Delegate last name is required")
      : schem;
  }),
  delegatePhone: yup.string().when(["hasDelegatePresent", "delegateCanAct"], ([hasDelegatePresent, delegateCanAct], schem) => {
    return hasDelegatePresent && delegateCanAct
      ? optional(schem.phone())
      : schem;
  }),
  delegateEmail: yup.string().trim().when(["hasDelegatePresent", "delegateCanAct"], ([hasDelegatePresent, delegateCanAct], schem) => {
    return hasDelegatePresent && delegateCanAct
      ? optional(schem.email("Invalid email format"))
      : schem;
  }),
  delegateRelationship: yup.string().trim(),
  delegateCanAct: yup.boolean(),
  // Next of kin - conditional validation: if any field is filled, all three must be filled
  nextOfKinFirstName: yup.string().trim().test("nok-all-or-none", "Please fill all next of kin fields or leave them all blank", function(value) {
    const { nextOfKinLastName, nextOfKinRelationship } = this.parent;
    return validateNextOfKin(value, nextOfKinLastName, nextOfKinRelationship);
  }),
  nextOfKinLastName: yup.string().trim().test("nok-all-or-none", "Please fill all next of kin fields or leave them all blank", function(value) {
    const { nextOfKinFirstName, nextOfKinRelationship } = this.parent;
    return validateNextOfKin(nextOfKinFirstName, value, nextOfKinRelationship);
  }),
  nextOfKinRelationship: yup.string().trim().test("nok-all-or-none", "Please fill all next of kin fields or leave them all blank", function(value) {
    const { nextOfKinFirstName, nextOfKinLastName } = this.parent;
    return validateNextOfKin(nextOfKinFirstName, nextOfKinLastName, value);
  }),
  // GP Details - conditional validation: if any field is filled, all three must be filled
  gpClinicName: yup.string().trim().test("gp-all-or-none", "Please fill all GP details fields or leave them all blank", function(value) {
    const { gpDoctorFirstName, gpDoctorLastName } = this.parent;
    return validateGPDetails(value, gpDoctorFirstName, gpDoctorLastName);
  }),
  gpDoctorFirstName: yup.string().trim().test("gp-all-or-none", "Please fill all GP details fields or leave them all blank", function(value) {
    const { gpClinicName, gpDoctorLastName } = this.parent;
    return validateGPDetails(gpClinicName, value, gpDoctorLastName);
  }),
  gpDoctorLastName: yup.string().trim().test("gp-all-or-none", "Please fill all GP details fields or leave them all blank", function(value) {
    const { gpClinicName, gpDoctorFirstName } = this.parent;
    return validateGPDetails(gpClinicName, gpDoctorFirstName, value);
  }),
  // Triage change reason - optional in create schema
  triageChangeReason: yup.string().trim().optional(),
});

const createEditSchema = (currentPatientId?: string) => yup.object().shape({
  ...baseSchema,
  firstName: yup.string().trim().ensure().when("submissionType", ([submissionType], schem) => {
    return submissionType !== "unknown"
      ? schem.required("First name is required")
      : schem;
  }),
  lastName: yup.string().trim().ensure().when("submissionType", ([submissionType], schem) => {
    return submissionType !== "unknown"
      ? schem.required("Last name is required")
      : schem;
  }),
  phone: optional(yup.string().phone()),
  email: createEmailSchemaWithDuplicateCheck(currentPatientId),
  // Delegate fields - optional
  hasDelegatePresent: yup.boolean(),
  delegateFirstName: yup.string().trim().when(["hasDelegatePresent", "delegateCanAct"], ([hasDelegatePresent, delegateCanAct], schem) => {
    return hasDelegatePresent && delegateCanAct
      ? schem.required("Delegate first name is required")
      : schem;
  }),
  delegateLastName: yup.string().trim().when(["hasDelegatePresent", "delegateCanAct"], ([hasDelegatePresent, delegateCanAct], schem) => {
    return hasDelegatePresent && delegateCanAct
      ? schem.required("Delegate last name is required")
      : schem;
  }),
  delegatePhone: yup.string().when(["hasDelegatePresent", "delegateCanAct"], ([hasDelegatePresent, delegateCanAct], schem) => {
    return hasDelegatePresent && delegateCanAct
      ? optional(schem.phone())
      : schem;
  }),
  delegateEmail: yup.string().trim().when(["hasDelegatePresent", "delegateCanAct"], ([hasDelegatePresent, delegateCanAct], schem) => {
    return hasDelegatePresent && delegateCanAct
      ? optional(schem.email("Invalid email format"))
      : schem;
  }),
  delegateRelationship: yup.string().trim(),
  delegateCanAct: yup.boolean(),
  // Next of kin - conditional validation: if any field is filled, all three must be filled
  nextOfKinFirstName: yup.string().trim().test("nok-all-or-none", "Please fill all next of kin fields or leave them all blank", function(value) {
    const { nextOfKinLastName, nextOfKinRelationship } = this.parent;
    return validateNextOfKin(value, nextOfKinLastName, nextOfKinRelationship);
  }),
  nextOfKinLastName: yup.string().trim().test("nok-all-or-none", "Please fill all next of kin fields or leave them all blank", function(value) {
    const { nextOfKinFirstName, nextOfKinRelationship } = this.parent;
    return validateNextOfKin(nextOfKinFirstName, value, nextOfKinRelationship);
  }),
  nextOfKinRelationship: yup.string().trim().test("nok-all-or-none", "Please fill all next of kin fields or leave them all blank", function(value) {
    const { nextOfKinFirstName, nextOfKinLastName } = this.parent;
    return validateNextOfKin(nextOfKinFirstName, nextOfKinLastName, value);
  }),
  // GP Details - conditional validation: if any field is filled, all three must be filled
  gpClinicName: yup.string().trim().test("gp-all-or-none", "Please fill all GP details fields or leave them all blank", function(value) {
    const { gpDoctorFirstName, gpDoctorLastName } = this.parent;
    return validateGPDetails(value, gpDoctorFirstName, gpDoctorLastName);
  }),
  gpDoctorFirstName: yup.string().trim().test("gp-all-or-none", "Please fill all GP details fields or leave them all blank", function(value) {
    const { gpClinicName, gpDoctorLastName } = this.parent;
    return validateGPDetails(gpClinicName, value, gpDoctorLastName);
  }),
  gpDoctorLastName: yup.string().trim().test("gp-all-or-none", "Please fill all GP details fields or leave them all blank", function(value) {
    const { gpClinicName, gpDoctorFirstName } = this.parent;
    return validateGPDetails(gpClinicName, gpDoctorFirstName, value);
  }),
  // Triage change reason - optional in edit schema (validation handled in component)
  triageChangeReason: yup.string().trim().optional(),
});

// Initial form values
const initialState = {
  submissionType: "",
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  dob: undefined,
  sex: "",
  gender: "",
  pronouns: "",
  medicareNo: "",
  medicareNoPos: "",
  umrn: "",
  infectiousSymptoms: [],
  tasks: [],
  problem: "",
  pain: "",
  frailty: "",
  atsScore: "",
  triageChangeReason: "",
  location: "",
  typeOfVisit: "",
  modeOfArrival: "",
  ambulanceCaseNumber: "",
  triageDirection: "",
  patientOrigin: "",
  nursingAssessment: "",
  complaintCode: "",
  allergies: [],
  noAllergies: false, // Default to "Patient has no known allergies"
  alcoholDrugs: false,
  fdv: false,
  represent: false,
  injuries: false,
  hasDelegatePresent: false,
  delegateId: "",
  delegateFirstName: "",
  delegateLastName: "",
  delegatePhone: "",
  delegateEmail: "",
  delegateRelationship: "",
  delegateCanAct: false,
  // New fields for NMHS configuration
  stream: "",
  fdvRelated: false,
  representIn48h: false,
  injury: false,
  cause: "",
  intent: "",
  relatedAlcoholDrugs: false,
  anonymous: false,
  unknown: false,
  // New patient fields
  gpClinicName: "",
  gpDoctorFirstName: "",
  gpDoctorLastName: "",
  nextOfKinFirstName: "",
  nextOfKinLastName: "",
  nextOfKinRelationship: "",
  previousNames: [],
  _isEditMode: false,
  _encounterId: undefined,
  _encounterOriginalStatus: "",
  _resumeExpectedEncounter: false,
  _existingPatientId: undefined,
  _preExistingPatientSnapshot: undefined,
  _externalRegisterSummary: {
    hasExternalRegisterAlert: false,
    externalRegisterAlerts: [],
  },
  _patientStatusRecentEncounters: undefined,
};

// Helper functions (copied from addPatientContext)
const ensureString = (value: any): string => {
  if (typeof value === "string") return value ?? "";
  if (typeof value === "number") return String(value);
  return "";
};

export const encounterToFormValues = (encounter: PatientEncounter) => {
  const patient = encounter.patient || {};
  const encounterAny = encounter as any; // Type assertion for new backend fields

  // Extract delegate information from the delegates array
  const delegate = encounter.delegates?.[0]; // Get the first delegate if any
  const hasDelegatePresent = !!delegate;

  // Ensure all tasks are present (similar to patient.tsx)
  // Get task definitions from settings to ensure all tasks are initialized
  // This will be done in the component where we have access to settings context
  const loadedTasks = encounter.tasks || [];

  return {
    ...patientToGenericFormValues(patient),
    infectiousSymptoms: encounter.infectiousSymptoms || [],
    tasks: loadedTasks, // Will be normalized in the component
    expects: encounter.problem?.expects || "",
    patientComplaint: encounter.problem?.patient || "", // patientComplaint maps from problem.patient
    problem: encounter.problem?.triage || "", // presentingComplaint maps from problem.triage
    complaintCode: encounterAny.complaintCode || "", // Extract complaintCode from encounter
    nursingAssessment: encounter.problem?.nursingAssessment || "", // nursingAssessment maps from problem.triage
    pain: ensureString(encounter.pain),
    frailty: ensureString(encounter.frailty),
    atsScore: ensureString(encounter.atsScore),
    location: encounter.location?.id || "",
    typeOfVisit: encounterAny.typeOfVisit || "",
    modeOfArrival: encounterAny.modeOfArrival || "",
    ambulanceCaseNumber: encounterAny.ambulanceCaseNumber || "",
    triageDirection: encounterAny.triageDirection || "",
    patientOrigin: encounterAny.patientOrigin || "",
    // Delegate fields
    hasDelegatePresent,
    delegateId: delegate?.id || "",
    delegateFirstName: delegate?.firstName || "",
    delegateLastName: delegate?.lastName || "",
    delegatePhone: ((delegate as any)?.phone ?? (delegate as any)?.phone_number) || "",
    delegateEmail: delegate?.email || "",
    delegateRelationship: delegate?.relationship || "",
    delegateCanAct: (delegate as any)?.canAct === true, // Default to false if not set
    // New fields for NMHS configuration
    stream: encounterAny.stream || "",
    fdvRelated: encounterAny.fdvRelated || false,
    representIn48h: encounterAny.representIn48h || false,
    injury: encounterAny.injury || false,
    pronouns: patient.pronouns || "",
    cause: encounterAny.cause || "",
    intent: encounterAny.intent || "",
    relatedAlcoholDrugs: encounterAny.relatedAlcoholDrugs || false,
    anonymous: patient.anonymous || false,
    unknown: patient.unknown || false,
    triaged: encounter.triaged || undefined,
    triageChangeReason: encounterAny.triageChangeReason || "",
  };
};

// Styles
const styles = {
  container: (theme: Theme) => css`
    background-color: ${theme.palette.primary[50]};
  `,
};

const SECURED_FIELD_AUTHORIZATION_REQUEST = {
  reason: "secured-field-change",
  title: "Security confirmation required",
  message: "You changed a secured field. Confirm clinician and operator code to continue this save.",
} as const;

const createGenericPatientFormResolver = ({
  configId,
  encounterPatientId,
  hasEncounter,
  mode,
}: {
  configId: string;
  encounterPatientId?: string;
  hasEncounter: boolean;
  mode: "create" | "edit";
}) => {
  const resolverCache = new Map<string, any>();

  return (values: Record<string, any>, context: any, options: any) => {
    const patientId = values._existingPatientId || encounterPatientId;
    const isEdit = values._isEditMode || !!patientId || mode === "edit" || hasEncounter;
    const resolverMode = configId === "concierge"
      ? "concierge"
      : (isEdit ? "edit" : "create");
    const schemaKey = [
      configId,
      resolverMode,
      patientId ?? "",
    ].join("|");

    let resolver = resolverCache.get(schemaKey);
    if (!resolver) {
      const schema = configId === "concierge"
        ? createConciergeSchema as any
        : (isEdit ? createEditSchema(patientId) : createSchema);
      resolver = yupResolver(schema);
      resolverCache.set(schemaKey, resolver);
    }

    return resolver(values, context, options);
  };
};

const ValidationErrorBanner: React.FC<{ control: any }> = ({ control }) => {
  const { errors } = useFormState({ control });
  const hasValidationErrors = Object.keys(errors).length > 0;

  if (!hasValidationErrors) {
    return <div style={{ height: "50px", marginBottom: "16px" }} />;
  }

  return (
    <Alert severity="error" sx={{ mb: 2 }}>
      Please fix the validation errors below before submitting the form.
    </Alert>
  );
};

const GenericAddPatientFormEffects: React.FC<{
  methods: any;
  normalizeTasks: (tasks: Task[]) => Task[];
  settings: any;
  currentEditMode: boolean;
  setCurrentEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  temporaryDelegateData: {
    delegateId?: string;
    delegateFirstName?: string;
    delegateLastName?: string;
    delegatePhone?: string;
    delegateEmail?: string;
    delegateRelationship?: string;
    delegateCanAct?: boolean;
  } | null;
  setTemporaryDelegateData: React.Dispatch<React.SetStateAction<{
    delegateId?: string;
    delegateFirstName?: string;
    delegateLastName?: string;
    delegatePhone?: string;
    delegateEmail?: string;
    delegateRelationship?: string;
    delegateCanAct?: boolean;
  } | null>>;
  onEncounterIdChange: (encounterId?: string) => void;
}> = ({
  methods,
  normalizeTasks,
  settings,
  currentEditMode,
  setCurrentEditMode,
  temporaryDelegateData,
  setTemporaryDelegateData,
  onEncounterIdChange,
}) => {
  const control = methods.control;
  const isEditMode = useWatch({ control, name: "_isEditMode" });
  const encounterId = useWatch({ control, name: "_encounterId" });
  const hasDelegatePresent = useWatch({ control, name: "hasDelegatePresent" });
  const gpValues = useWatch({
    control,
    name: ["gpClinicName", "gpDoctorFirstName", "gpDoctorLastName"],
  }) as unknown[];
  const nextOfKinValues = useWatch({
    control,
    name: ["nextOfKinFirstName", "nextOfKinLastName", "nextOfKinRelationship"],
  }) as unknown[];
  const previousNames = useWatch({ control, name: "previousNames" });

  React.useEffect(() => {
    onEncounterIdChange(encounterId || undefined);
  }, [encounterId, onEncounterIdChange]);

  React.useEffect(() => {
    if (isEditMode && !currentEditMode) {
      setCurrentEditMode(true);
    }
  }, [isEditMode, currentEditMode, setCurrentEditMode]);

  React.useEffect(() => {
    const currentTasks = methods.getValues("tasks");
    if (!Array.isArray(currentTasks) || currentTasks.length === 0) {
      const normalized = normalizeTasks(currentTasks || []);
      methods.setValue("tasks", normalized, { shouldDirty: false });
    }
  }, [settings?.taskDefinitions, methods, normalizeTasks]);

  React.useEffect(() => {
    if (hasDelegatePresent === false) {
      const currentDelegateData = {
        delegateId: methods.getValues("delegateId"),
        delegateFirstName: methods.getValues("delegateFirstName"),
        delegateLastName: methods.getValues("delegateLastName"),
        delegatePhone: methods.getValues("delegatePhone"),
        delegateEmail: methods.getValues("delegateEmail"),
        delegateRelationship: methods.getValues("delegateRelationship"),
        delegateCanAct: methods.getValues("delegateCanAct"),
      };
      if (currentDelegateData.delegateFirstName || currentDelegateData.delegateLastName) {
        setTemporaryDelegateData(previousDelegateData => (
          isEqual(previousDelegateData, currentDelegateData)
            ? previousDelegateData
            : currentDelegateData
        ));
      }
    } else if (hasDelegatePresent === true && temporaryDelegateData) {
      methods.setValue("delegateId", temporaryDelegateData.delegateId || "", { shouldDirty: false });
      methods.setValue("delegateFirstName", temporaryDelegateData.delegateFirstName || "", { shouldDirty: false });
      methods.setValue("delegateLastName", temporaryDelegateData.delegateLastName || "", { shouldDirty: false });
      methods.setValue("delegatePhone", temporaryDelegateData.delegatePhone || "", { shouldDirty: false });
      methods.setValue("delegateEmail", temporaryDelegateData.delegateEmail || "", { shouldDirty: false });
      methods.setValue("delegateRelationship", temporaryDelegateData.delegateRelationship || "", { shouldDirty: false });
      methods.setValue("delegateCanAct", temporaryDelegateData.delegateCanAct || false, { shouldDirty: false });
    }
  }, [hasDelegatePresent, methods, temporaryDelegateData, setTemporaryDelegateData]);

  React.useEffect(() => {
    if (
      methods.formState.isSubmitted ||
      methods.formState.touchedFields.gpClinicName ||
      methods.formState.touchedFields.gpDoctorFirstName ||
      methods.formState.touchedFields.gpDoctorLastName
    ) {
      void methods.trigger(["gpClinicName", "gpDoctorFirstName", "gpDoctorLastName"]);
    }
  }, [gpValues[0], gpValues[1], gpValues[2], methods]);

  React.useEffect(() => {
    if (
      methods.formState.isSubmitted ||
      methods.formState.touchedFields.nextOfKinFirstName ||
      methods.formState.touchedFields.nextOfKinLastName ||
      methods.formState.touchedFields.nextOfKinRelationship
    ) {
      void methods.trigger(["nextOfKinFirstName", "nextOfKinLastName", "nextOfKinRelationship"]);
    }
  }, [nextOfKinValues[0], nextOfKinValues[1], nextOfKinValues[2], methods]);

  React.useEffect(() => {
    if (methods.formState.isSubmitted || methods.formState.touchedFields.previousNames) {
      void methods.trigger("previousNames");
    }
  }, [previousNames, methods]);

  return null;
};

const GenericAddPatientFormBodyComponent: React.FC<{
  methods: any;
  normalizeTasks: (tasks: Task[]) => Task[];
  settings: any;
  currentEditMode: boolean;
  setCurrentEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  temporaryDelegateData: {
    delegateId?: string;
    delegateFirstName?: string;
    delegateLastName?: string;
    delegatePhone?: string;
    delegateEmail?: string;
    delegateRelationship?: string;
    delegateCanAct?: boolean;
  } | null;
  setTemporaryDelegateData: React.Dispatch<React.SetStateAction<{
    delegateId?: string;
    delegateFirstName?: string;
    delegateLastName?: string;
    delegatePhone?: string;
    delegateEmail?: string;
    delegateRelationship?: string;
    delegateCanAct?: boolean;
  } | null>>;
  onEncounterIdChange: (encounterId?: string) => void;
  onOpenExternalRegisterDetails?: (patientId: string, alertId?: string) => void;
  configId: string;
  encounter?: PatientEncounter;
}> = ({
  methods,
  normalizeTasks,
  settings,
  currentEditMode,
  setCurrentEditMode,
  temporaryDelegateData,
  setTemporaryDelegateData,
  onEncounterIdChange,
  onOpenExternalRegisterDetails,
  configId,
  encounter,
}) => {
  const config = React.useMemo(() => getFormConfig(configId), [configId]);
  const complaintCode = useWatch({ control: methods.control, name: "complaintCode" });
  const injuryIsRequired = configId === "nmhs" && isInjuryComplaintCode((settings as any)?.complaintCodes || [], complaintCode);
  const isConciergeConfig = configId === "concierge";
  const canRenderExternalRegisterClinical = canRenderExternalRegisterClinicalIndicator({
    isConciergeMode: isConciergeConfig,
  });
  const canOpenExternalRegisterSecureDetails = canOpenExternalRegisterDetails({
    isConciergeMode: isConciergeConfig,
  });
  const isAllergyBlocked = !!encounter && (encounter.status === "discharged" || encounter.status === "disabled");
  const allergyBlockedReason = !encounter ? undefined : (
    encounter.status === "discharged"
      ? "Allergies cannot be modified for a discharged encounter."
      : encounter.status === "disabled"
        ? "This encounter is disabled."
        : undefined
  );

  React.useEffect(() => {
    if (injuryIsRequired && methods.getValues("injury") !== true) {
      methods.setValue("injury", true, { shouldDirty: true, shouldValidate: true });
    }
  }, [injuryIsRequired, methods]);

  const disabledFields = React.useMemo(() => ({
    allergies: { disabled: isAllergyBlocked, reason: allergyBlockedReason },
    injuries: { disabled: injuryIsRequired, reason: injuryIsRequired ? "Injury is automatically selected for this complaint code." : undefined },
  }), [allergyBlockedReason, injuryIsRequired, isAllergyBlocked]);
  const patientStatusRuntime = React.useMemo(() => ({
    canRenderExternalRegisterClinical,
    canOpenExternalRegisterSecureDetails,
    currentEncounterId: encounter?.id,
    onOpenExternalRegisterDetails,
  }), [
    canOpenExternalRegisterSecureDetails,
    canRenderExternalRegisterClinical,
    encounter?.id,
    onOpenExternalRegisterDetails,
  ]);

  return (
    <FormProvider {...methods}>
      <GenericAddPatientFormEffects
        methods={methods}
        normalizeTasks={normalizeTasks}
        settings={settings}
        currentEditMode={currentEditMode}
        setCurrentEditMode={setCurrentEditMode}
        temporaryDelegateData={temporaryDelegateData}
        setTemporaryDelegateData={setTemporaryDelegateData}
        onEncounterIdChange={onEncounterIdChange}
      />
      <DialogContent css={styles.container} sx={{ p: 4, pt: 1 }}>
        <Box
          component="form"
          autoComplete="off"
          onSubmit={(event) => event.preventDefault()}
        >
          <PatientStatusWidgetRuntimeProvider value={patientStatusRuntime}>
            {config ? (
              <GenericFormRenderer
                config={config}
                disabledFields={disabledFields}
              />
            ) : null}
          </PatientStatusWidgetRuntimeProvider>
        </Box>
      </DialogContent>
    </FormProvider>
  );
};

const GenericAddPatientFormBody = React.memo(GenericAddPatientFormBodyComponent);

// Props interface
interface GenericAddPatientModalProps {
  mode?: "create" | "edit";
  encounter?: PatientEncounter;
  configId?: string; // Config ID to use (defaults to "nmhs")
}

// Main modal content component
const GenericAddPatientModalContent: React.FC<GenericAddPatientModalProps> = ({
  mode = "create",
  encounter,
  configId = "nmhs"
}) => {
  const { action: createAction } = encounterService.useMutateResource(undefined, {});
  const { mutateAsync: updateAction } = encounterService.useUpdate();
  const openObservationModal = useAddObservationModal();
  const { popModal } = useModal();
  const notify = useNotify();
  const { requestAuthorization, AuthorizationDialog } = useSensitiveActionAuthorization();
  const {
    detailsModalState,
    openExternalRegisterDetails,
    closeExternalRegisterDetails,
  } = useExternalRegisterSecureDetailsModalState();
  const [shouldConfirmClose, setShouldConfirmClose] = React.useState(true);
  const [currentEditMode, setCurrentEditMode] = React.useState(mode === "edit");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isOpeningObservation, setIsOpeningObservation] = React.useState(false);
  const { settings } = useSettingsContext();

  // Store original values when loading an encounter for edit mode
  const [originalValues, setOriginalValues] = React.useState<any>(null);
  const [deferredMevState, setDeferredMevState] = React.useState<MevSummaryDeferredState>(
    initialMevSummaryDeferredState,
  );
  const lastEncounterId = React.useRef<string | undefined>(undefined);
  // Store temporary delegate data when hasDelegatePresent is set to false
  // This allows restoring the data if user switches back to true
  const [temporaryDelegateData, setTemporaryDelegateData] = React.useState<{
    delegateId?: string;
    delegateFirstName?: string;
    delegateLastName?: string;
    delegatePhone?: string;
    delegateEmail?: string;
    delegateRelationship?: string;
    delegateCanAct?: boolean;
  } | null>(null);
  const [activeEncounterId, setActiveEncounterId] = React.useState<string | undefined>(encounter?.id);

  // Normalize tasks to ensure all task definitions are present
  const normalizeTasks = React.useCallback((tasks: Task[]): Task[] => {
    // Get task definitions from settings
    const taskDefinitions: any[] = (settings as any)?.taskDefinitions || [];

    if (taskDefinitions.length > 0) {
      // Ensure all task definitions have corresponding tasks
      return taskDefinitions.map(td => {
        const existingTask = tasks.find(t => t.type === td.name);
        return existingTask || { type: td.name as TaskType, status: "not_requested" as const };
      });
    } else {
      // Fallback to TaskType enum if no task definitions
      return TaskType.map(type => {
        const existingTask = tasks.find(t => t.type === type);
        return existingTask || { type, status: "not_requested" as const };
      });
    }
  }, [settings]);

  // Memoize initial values to prevent re-renders
  const initialValues = React.useMemo(() => {
    if (encounter) {
      const formValues = encounterToFormValues(encounter);
      // Normalize tasks to ensure all are present
      formValues.tasks = normalizeTasks(formValues.tasks);
      // Extract patient ID - handles both object and string patient references
      const patientId = encounter.patient?.id ||
        (typeof encounter.patient === "string" ? encounter.patient : undefined);
      const valuesWithMeta = {
        ...formValues,
        _isEditMode: true,
        _encounterId: encounter.id,
        _existingPatientId: patientId, // Required for email duplicate validation to exclude this patient
        _encounterOriginalStatus: encounter.status || "",
        _resumeExpectedEncounter: encounter.status === "expected",
      } as any;
      return valuesWithMeta;
    }
    // For new encounters, also normalize tasks to ensure all tasks are present
    return {
      ...initialState,
      tasks: normalizeTasks([])
    };
  }, [encounter, normalizeTasks]);

  // Store original values after form is initialized (in useEffect to avoid render issues)
  React.useEffect(() => {
    const encounterId = encounter?.id;
    if (encounter && encounterId && encounterId !== lastEncounterId.current) {
      // Compute values when encounter changes
      const formValues = encounterToFormValues(encounter);
      formValues.tasks = normalizeTasks(formValues.tasks);
      // Extract patient ID - handles both object and string patient references
      const patientId = encounter.patient?.id ||
        (typeof encounter.patient === "string" ? encounter.patient : undefined);
      const valuesWithMeta = {
        ...formValues,
        _isEditMode: true,
        _encounterId: encounter.id,
        _existingPatientId: patientId, // Required for email duplicate validation to exclude this patient
        _encounterOriginalStatus: encounter.status || "",
        _resumeExpectedEncounter: encounter.status === "expected",
      } as any;
      setOriginalValues(valuesWithMeta);
      lastEncounterId.current = encounterId;
      // Initialize temporary delegate data if encounter has a delegate
      if (valuesWithMeta.hasDelegatePresent && (valuesWithMeta.delegateFirstName || valuesWithMeta.delegateLastName)) {
        setTemporaryDelegateData({
          delegateId: valuesWithMeta.delegateId,
          delegateFirstName: valuesWithMeta.delegateFirstName,
          delegateLastName: valuesWithMeta.delegateLastName,
          delegatePhone: valuesWithMeta.delegatePhone,
          delegateEmail: valuesWithMeta.delegateEmail,
          delegateRelationship: valuesWithMeta.delegateRelationship,
          delegateCanAct: valuesWithMeta.delegateCanAct,
        });
      } else {
        // Clear temporary delegate data if no delegate
        setTemporaryDelegateData(null);
      }
    }
  }, [encounter?.id, normalizeTasks]); // Only depend on encounter ID and normalizeTasks

  const encounterPatientId = encounter?.patient?.id ||
    (typeof encounter?.patient === "string" ? encounter.patient : undefined);
  const formResolver = React.useMemo(() => createGenericPatientFormResolver({
    configId,
    encounterPatientId,
    hasEncounter: !!encounter,
    mode,
  }), [configId, encounterPatientId, mode, !!encounter]);

  // Initialize form with useForm for more control
  const methods = useForm({
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: initialValues,
    resolver: formResolver,
  });

  const { handleSubmit } = methods;

  React.useEffect(() => {
    setActiveEncounterId(encounter?.id);
  }, [encounter?.id]);

  const handleEncounterIdChange = React.useCallback((nextEncounterId?: string) => {
    setActiveEncounterId((currentEncounterId) => (
      currentEncounterId === nextEncounterId ? currentEncounterId : nextEncounterId
    ));
  }, []);

  const liveEncounterId = encounter?.id || activeEncounterId;
  const { data: liveEncounter } = encounterService.useGetOne(liveEncounterId || "", {
    enabled: !!liveEncounterId,
  });
  const hasObservations = (liveEncounter?.observations?.length ?? 0) > 0;

  // Helper function to deeply compare values, handling special cases
  const valuesAreEqual = (original: any, current: any): boolean => {
    // Handle moment objects
    if (original && typeof original === "object" && "format" in original && current && typeof current === "object" && "format" in current) {
      return original.format("YYYY-MM-DD") === current.format("YYYY-MM-DD");
    }
    // Handle undefined vs empty string (they should be treated as equal for comparison)
    const origNormalized = original === "" ? undefined : original;
    const currNormalized = current === "" ? undefined : current;
    // Use lodash isEqual for deep comparison
    return isEqual(origNormalized, currNormalized);
  };

  const fieldIdToFormKey = React.useMemo<Record<string, string>>(() => ({
    presentingComplaint: "problem",
    triageCategory: "atsScore",
    painScore: "pain",
    sexAtBirth: "sex",
    dateOfBirth: "dob",
    medicareNumber: "medicareNo",
    medicarePosition: "medicareNoPos",
  }), []);

  const extractFieldMetadata = React.useCallback((config: ReturnType<typeof getFormConfig>) => {
    const visibleFieldIds = new Set<string>();
    const securityFieldIds = new Set<string>();
    if (!config) return { visibleFieldIds, securityFieldIds };

    const extractFromFields = (fields: any[]) => {
      for (const field of fields) {
        if (typeof field === "string") {
          const formKey = fieldIdToFormKey[field] || field;
          visibleFieldIds.add(formKey);
        } else if (field && typeof field === "object") {
          if (field.id) {
            const formKey = fieldIdToFormKey[field.id] || field.id;
            visibleFieldIds.add(formKey);
            if (field.securityEnabled === true) {
              securityFieldIds.add(formKey);
            }
          }
          if (field.fields && Array.isArray(field.fields)) {
            extractFromFields(field.fields);
          }
        }
      }
    };

    const extractFromGroups = (groups: any[]) => {
      for (const group of groups) {
        if (group.fields && Array.isArray(group.fields)) {
          extractFromFields(group.fields);
        }
      }
    };

    const extractFromSections = (sections: any[]) => {
      for (const section of sections) {
        if (section.groups && Array.isArray(section.groups)) {
          extractFromGroups(section.groups);
        }
      }
    };

    if (config.columns && Array.isArray(config.columns)) {
      for (const column of config.columns) {
        if (column.sections && Array.isArray(column.sections)) {
          extractFromSections(column.sections);
        }
      }
    }

    if (config.sections && Array.isArray(config.sections)) {
      extractFromSections(config.sections);
    }

    return { visibleFieldIds, securityFieldIds };
  }, [fieldIdToFormKey]);

  // Helper function to extract all field IDs from a form config
  // This is used to determine which fields are visible and can be cleared
  const extractVisibleFieldIds = React.useCallback((config: ReturnType<typeof getFormConfig>): Set<string> => {
    return extractFieldMetadata(config).visibleFieldIds;
  }, [extractFieldMetadata]);

  // Filter payload to only include changed fields
  // visibleFields: Set of field IDs that are visible in the current form config
  const filterChangedFields = React.useCallback((currentPayload: any, originalFormValues: any, currentFormData: any, visibleFields: Set<string>): any => {
    if (!originalFormValues) return currentPayload;

    const filtered: any = {};

    // For patient payload, compare each field
    if (currentPayload.patient) {
      const patientFiltered: any = {};
      const originalPatient = originalFormValues;

      // Helper to derive anonymous/unknown from submissionType
      const deriveAnonymousUnknown = (submissionType: string) => {
        if (submissionType === "anonymous") {
          return { anonymous: true, unknown: false };
        } else if (submissionType === "unknown") {
          return { anonymous: false, unknown: true };
        } else {
          return { anonymous: false, unknown: false };
        }
      };

      // Helper to reconstruct nested objects from flat form fields
      const reconstructGpDetails = (formData: any) => {
        if (!formData.gpClinicName && !formData.gpDoctorFirstName && !formData.gpDoctorLastName) {
          return undefined;
        }
        return {
          clinicName: formData.gpClinicName || "",
          doctorFirstName: formData.gpDoctorFirstName || "",
          doctorLastName: formData.gpDoctorLastName || "",
        };
      };

      const reconstructNextOfKin = (formData: any) => {
        if (!formData.nextOfKinFirstName && !formData.nextOfKinLastName && !formData.nextOfKinRelationship) {
          return undefined;
        }
        return {
          firstName: formData.nextOfKinFirstName || "",
          lastName: formData.nextOfKinLastName || "",
          relationship: formData.nextOfKinRelationship || "",
        };
      };

      // Check nested fields that might not be in currentPayload.patient if they were cleared
      const nestedFieldsToCheck = ["gpDetails", "nextOfKin", "previousNames"];
      nestedFieldsToCheck.forEach(key => {
        if (key === "gpDetails") {
          const origGpDetails = reconstructGpDetails(originalPatient);
          const currGpDetails = reconstructGpDetails(currentFormData);
          if (!isEqual(origGpDetails, currGpDetails)) {
            // Include the value from currentPayload if it exists, otherwise include undefined to clear it
            patientFiltered[key] = currentPayload.patient[key] !== undefined
              ? currentPayload.patient[key]
              : undefined;
          }
        } else if (key === "nextOfKin") {
          const origNextOfKin = reconstructNextOfKin(originalPatient);
          const currNextOfKin = reconstructNextOfKin(currentFormData);
          if (!isEqual(origNextOfKin, currNextOfKin)) {
            patientFiltered[key] = currentPayload.patient[key] !== undefined
              ? currentPayload.patient[key]
              : undefined;
          }
        } else if (key === "previousNames") {
          const origPreviousNames = Array.isArray(originalPatient.previousNames) ? originalPatient.previousNames : [];
          const currPreviousNames = Array.isArray(currentFormData.previousNames) ? currentFormData.previousNames : [];
          if (!isEqual(origPreviousNames, currPreviousNames)) {
            patientFiltered[key] = currentPayload.patient[key] !== undefined
              ? currentPayload.patient[key]
              : undefined;
          }
        }
      });

      // Build a list of all patient fields to check - both from payload and from original values
      // This ensures we detect fields that were cleared (removed from payload but had original values)
      const patientFieldsToCheck = new Set([
        ...Object.keys(currentPayload.patient),
        // Also check fields that might have been cleared (had value originally but now empty)
        "firstName", "lastName", "phone", "email", "sex", "gender", "pronouns",
        "medicareNo", "medicareNoPos", "umrn"
      ]);

      patientFieldsToCheck.forEach(key => {
        // Skip nested fields - already handled above
        if (nestedFieldsToCheck.includes(key)) {
          return;
        }

        const formKey = key === "dateOfBirth" ? "dob" : key;
        let originalVal = originalPatient[formKey];
        let currentVal = currentFormData[formKey];

        // Special handling for anonymous/unknown flags - derive from submissionType
        if (key === "anonymous" || key === "unknown") {
          const originalDerived = deriveAnonymousUnknown(originalPatient.submissionType || "");
          const currentDerived = deriveAnonymousUnknown(currentFormData.submissionType || "");
          originalVal = originalDerived[key];
          currentVal = currentDerived[key];
        }

        // For dateOfBirth, compare moment objects
        if (key === "dateOfBirth") {
          const originalDate = originalVal ? moment(originalVal).format("YYYY-MM-DD") : undefined;
          const currentDate = currentVal ? moment(currentVal).format("YYYY-MM-DD") : undefined;
          if (originalDate !== currentDate) {
            patientFiltered[key] = currentPayload.patient[key];
          }
        } else if (key === "allergies") {
          // Special handling for allergies array
          const origAllergies = Array.isArray(originalVal) ? originalVal : [];
          const currAllergies = Array.isArray(currentVal) ? currentVal : [];
          if (!isEqual(origAllergies, currAllergies)) {
            patientFiltered[key] = currentPayload.patient[key];
          }
        } else if (!valuesAreEqual(originalVal, currentVal)) {
          // Include the field if it's in currentPayload, or send "" if clearing a previously set value
          if (key in currentPayload.patient) {
            patientFiltered[key] = currentPayload.patient[key];
          } else {
            // Field was cleared: original had value but current is empty - send "" to clear it on backend
            // BUT only if the field is visible in the current form config (to avoid wiping fields not shown)
            const origNormalized = originalVal === "" ? undefined : originalVal;
            const currNormalized = currentVal === "" ? undefined : currentVal;
            const formKeyForVisibility = key === "dateOfBirth" ? "dob" : key;
            if (origNormalized !== undefined && currNormalized === undefined && visibleFields.has(formKeyForVisibility)) {
              patientFiltered[key] = "";
            }
          }
        }
      });

      if (Object.keys(patientFiltered).length > 0) {
        filtered.patient = patientFiltered;
      }
    }

    // For encounter payload, compare each field
    // We need to check both fields in currentPayload AND fields that might have been removed
    // - Empty → Value: Field will be in currentPayload (via Object.keys), so it's checked
    // - Value → Empty: Field won't be in currentPayload (removed by omitBy), so we need to explicitly check it
    // The hardcoded list ensures fields are always compared, even if removed from payload
    const fieldsToCheck = new Set([
      ...Object.keys(currentPayload),
      // Also check numeric fields that might have been removed from payload (value → empty case)
      "atsScore", "pain", "frailty",
      // Also check string fields that might have been cleared
      "typeOfVisit", "modeOfArrival", "ambulanceCaseNumber", "triageDirection",
      "patientOrigin", "location", "complaintCode", "stream", "cause", "intent",
      "triageChangeReason",
      // Also check array fields that might have been cleared
      "infectiousSymptoms"
    ]);

    fieldsToCheck.forEach(key => {
      if (key === "patient") return; // Already handled above

      const formKey = key === "problem" ? undefined : key; // problem is nested
      let originalVal: any;
      let currentFormVal: any;

      if (key === "problem") {
        // Compare problem object - must match buildEncounterSpecificPayload structure
        // Form field mappings:
        // - patientComplaint -> problem.patient
        // - problem (form field) -> problem.triage
        // - nursingAssessment -> problem.nursingAssessment
        // IMPORTANT: Only compare fields that are visible in the current form config
        // to avoid wiping data from fields that aren't shown (e.g., nursingAssessment in concierge mode)
        originalVal = {};
        currentFormVal = {};
        // patientComplaint maps to problem.patient
        if (visibleFields.has("patientComplaint")) {
          originalVal.patient = originalFormValues.patientComplaint || "";
          currentFormVal.patient = currentFormData.patientComplaint || "";
        }
        // problem form field maps to problem.triage (via presentingComplaint config field)
        if (visibleFields.has("problem")) {
          originalVal.triage = originalFormValues.problem || "";
          currentFormVal.triage = currentFormData.problem || "";
        }
        // nursingAssessment maps to problem.nursingAssessment
        if (visibleFields.has("nursingAssessment")) {
          originalVal.nursingAssessment = originalFormValues.nursingAssessment || "";
          currentFormVal.nursingAssessment = currentFormData.nursingAssessment || "";
        }
        // If no visible fields, skip problem comparison entirely
        if (Object.keys(originalVal).length === 0) {
          return; // Skip this iteration
        }
      } else if (key === "tasks") {
        // Tasks should always be compared
        originalVal = originalFormValues.tasks || [];
        currentFormVal = currentFormData.tasks || [];
      } else if (key === "infectiousSymptoms") {
        // Special handling for infectiousSymptoms array
        // Compare arrays and handle empty array case (needs to send empty array, not undefined)
        originalVal = Array.isArray(originalFormValues.infectiousSymptoms) ? originalFormValues.infectiousSymptoms : [];
        currentFormVal = Array.isArray(currentFormData.infectiousSymptoms) ? currentFormData.infectiousSymptoms : [];
      } else if (key === "delegates") {
        // Compare delegates
        const origHasDelegate = originalFormValues.hasDelegatePresent;
        const currHasDelegate = currentFormData.hasDelegatePresent;
        if (origHasDelegate !== currHasDelegate) {
          // Delegate presence changed - include the change in filtered payload
          // If removing delegate, ensure empty array is sent (even if not in currentPayload)
          if (origHasDelegate && !currHasDelegate) {
            filtered[key] = []; // Explicitly send empty array to clear delegates
          } else {
            // Adding delegate - use what's in currentPayload
            filtered[key] = currentPayload[key];
          }
          return;
        }
        if (currHasDelegate) {
          originalVal = {
            firstName: originalFormValues.delegateFirstName,
            lastName: originalFormValues.delegateLastName,
            phone: originalFormValues.delegatePhone,
            email: originalFormValues.delegateEmail,
            relationship: originalFormValues.delegateRelationship,
            canAct: originalFormValues.delegateCanAct || false
          };
          currentFormVal = {
            firstName: currentFormData.delegateFirstName,
            lastName: currentFormData.delegateLastName,
            phone: currentFormData.delegatePhone,
            email: currentFormData.delegateEmail,
            relationship: currentFormData.delegateRelationship,
            canAct: currentFormData.delegateCanAct || false
          };
        } else {
          return; // No delegate in both, skip
        }
      } else {
        originalVal = originalFormValues[formKey || key];
        currentFormVal = currentFormData[formKey || key];
      }

      // Compare values (normalize empty strings to undefined)
      const originalNormalized = originalVal === "" ? undefined : originalVal;
      const currentNormalized = currentFormVal === "" ? undefined : currentFormVal;

      // Special handling for array fields (infectiousSymptoms)
      if (key === "infectiousSymptoms") {
        // Use isEqual for deep array comparison
        if (!isEqual(originalVal, currentFormVal)) {
          // If the field exists in currentPayload, use it; otherwise send empty array to clear it
          // This handles the case where an array was changed to empty (removed from payload)
          if (key in currentPayload && currentPayload[key] !== undefined) {
            filtered[key] = currentPayload[key];
          } else {
            // Array was cleared: original had values but current is empty - send empty array to clear it
            filtered[key] = [];
          }
        }
        return; // Skip the rest of the comparison logic for array fields
      }

      // Special handling for numeric fields (atsScore, pain, frailty)
      if (["atsScore", "pain", "frailty"].includes(key)) {
        // Only process if the field is visible in the current form config
        if (!visibleFields.has(key)) {
          return; // Skip fields that aren't visible to avoid wiping data
        }
        const origNum = originalNormalized === undefined || originalNormalized === "" ? undefined : Number(originalNormalized);
        const currNum = currentNormalized === undefined || currentNormalized === "" ? undefined : Number(currentNormalized);
        if (origNum !== currNum) {
          // If the field exists in currentPayload, use it; otherwise use undefined to clear it
          // This handles the case where a value was changed to empty (removed from payload)
          filtered[key] = key in currentPayload ? currentPayload[key] : undefined;
        }
      } else if (!valuesAreEqual(originalNormalized, currentNormalized)) {
        // Include the field if it's in currentPayload, or send "" if clearing a previously set value
        if (key in currentPayload) {
          // Special handling for problem object - only include sub-fields that actually CHANGED
          // The backend will merge, so we only need to send what changed
          if (key === "problem" && currentPayload.problem) {
            const filteredProblem: any = {};
            // Only include patient if it's visible AND changed
            if (visibleFields.has("patientComplaint")) {
              const origPatient = originalFormValues.patientComplaint || "";
              const currPatient = currentFormData.patientComplaint || "";
              if (origPatient !== currPatient) {
                filteredProblem.patient = currentPayload.problem.patient;
              }
            }
            // Only include triage if it's visible AND changed
            if (visibleFields.has("problem")) {
              const origTriage = originalFormValues.problem || "";
              const currTriage = currentFormData.problem || "";
              if (origTriage !== currTriage) {
                filteredProblem.triage = currentPayload.problem.triage;
              }
            }
            // Only include nursingAssessment if it's visible AND changed
            if (visibleFields.has("nursingAssessment")) {
              const origNursing = originalFormValues.nursingAssessment || "";
              const currNursing = currentFormData.nursingAssessment || "";
              if (origNursing !== currNursing) {
                filteredProblem.nursingAssessment = currentPayload.problem.nursingAssessment;
              }
            }
            // Only include problem if there are actually changed fields
            if (Object.keys(filteredProblem).length > 0) {
              filtered[key] = filteredProblem;
            }
          } else {
            filtered[key] = currentPayload[key];
          }
        } else if (originalNormalized !== undefined && currentNormalized === undefined && visibleFields.has(key)) {
          // Field was cleared: original had value but current is empty - send "" to clear it on backend
          // BUT only if the field is visible in the current form config (to avoid wiping fields not shown)
          filtered[key] = "";
        }
      }
    });

    return filtered;
  }, []);

  // Function to load an encounter into edit mode
  const loadEncounter = React.useCallback((encounterToLoad: PatientEncounter) => {
    const formValues = encounterToFormValues(encounterToLoad);
    // Normalize tasks to ensure all are present
    formValues.tasks = normalizeTasks(formValues.tasks);
    // Extract patient ID - handles both object and string patient references
    const patientId = encounterToLoad.patient?.id ||
      (typeof encounterToLoad.patient === "string" ? encounterToLoad.patient : undefined);
    const valuesWithMeta = {
      ...formValues,
      _isEditMode: true,
      _encounterId: encounterToLoad.id,
      _existingPatientId: patientId, // Required for email duplicate validation to exclude this patient
      _encounterOriginalStatus: encounterToLoad.status || "",
      _resumeExpectedEncounter: encounterToLoad.status === "expected",
    } as any;
    methods.reset(valuesWithMeta);
    // Store original values for comparison on submit
    setOriginalValues(valuesWithMeta);
    lastEncounterId.current = encounterToLoad.id;
    setActiveEncounterId(encounterToLoad.id);
    setCurrentEditMode(true);
  }, [methods, normalizeTasks]);

  const hasModifiedSecurityField = React.useCallback((formData: any): boolean => {
    const config = getFormConfig(configId);
    const { securityFieldIds } = extractFieldMetadata(config);
    if (securityFieldIds.size === 0) return false;

    const baseline = originalValues ?? initialValues;
    if (!baseline) return false;

    for (const fieldKey of securityFieldIds) {
      if (!valuesAreEqual(baseline[fieldKey], formData[fieldKey])) {
        return true;
      }
    }
    return false;
  }, [configId, extractFieldMetadata, initialValues, originalValues, valuesAreEqual]);

  const createEncounterWithOptionalActAs = React.useCallback(async (payload: Partial<PatientEncounter>, actAsToken?: string) => {
    const cleanedPayload = omitBy(payload, (value) => value === "" && typeof value !== "boolean");
    if (!actAsToken) {
      return createAction(cleanedPayload);
    }

    const response = await global.client.post("encounters", cleanedPayload, {
      headers: {
        "X-ACT-AS": actAsToken,
      },
    });
    encounterService.invalideAllQueries();
    return (response as { data?: PatientEncounter })?.data;
  }, [createAction]);

  const updateEncounterWithOptionalActAs = React.useCallback(async (id: string, payload: Partial<PatientEncounter>, actAsToken?: string) => {
    if (!actAsToken) {
      return updateAction({ id, payload });
    }

    const response = await global.client.patch(`encounters/${id}`, payload, {
      headers: {
        "X-ACT-AS": actAsToken,
      },
    });
    const updated = (response as { data?: PatientEncounter })?.data;
    if (updated) {
      encounterService.updateCache(updated);
    } else {
      encounterService.invalideAllQueries();
    }
    return updated;
  }, [updateAction]);

  const maybeAutoOpenMevSummary = React.useCallback(async ({
    mode: saveMode,
    savedEncounter,
    currentFormValues,
  }: {
    mode: "create" | "update";
    savedEncounter?: PatientEncounter | null;
    currentFormValues: Record<string, any>;
  }) => {
    if (!savedEncounter) return;
    const shouldResumeExpectedEncounter = currentFormValues._resumeExpectedEncounter === true;

    const shouldAutoOpen = shouldAutoOpenMevSummaryAfterSave({
      mode: saveMode,
      savedEncounter,
      configId,
      resumeExpectedEncounter: shouldResumeExpectedEncounter,
      originalFormValues: originalValues,
      currentFormValues,
      config: mevSummaryAutoOpenConfig,
    });

    if (!shouldAutoOpen) return;

    try {
      await generateMevSummarySectionPdf(savedEncounter as any, settings);
    } catch (error) {
      console.error("Failed to auto-open MEV summary PDF", error);
    }
  }, [
    configId,
    originalValues,
    settings,
  ]);

  const activateInitialObservationDeferredFlow = React.useCallback((
    encounterForDeferred?: Pick<PatientEncounter, "id" | "atsScore"> | null,
    options?: {
      openOnExitWithoutObservationSave?: boolean;
    },
  ) => {
    setDeferredMevState((prev) => activateMevSummaryDeferredFlow(prev, encounterForDeferred, options));
  }, []);

  const markInitialObservationSavedForDeferredOpen = React.useCallback(() => {
    setDeferredMevState((prev) => markDeferredObservationSaved(prev));
  }, []);

  const maybeAutoOpenDeferredMevSummary = React.useCallback(async ({
    encounterOverride,
  }: {
    encounterOverride?: PatientEncounter | null;
  }) => {
    if (!canAttemptDeferredMevOpen(deferredMevState)) return false;
    const shouldResumeExpectedEncounter = methods.getValues("_resumeExpectedEncounter") === true;

    let savedEncounter = encounterOverride;

    if (!savedEncounter && deferredMevState.deferredEncounterId) {
      try {
        savedEncounter = await encounterService.getOne(deferredMevState.deferredEncounterId);
      } catch (error) {
        console.warn("Failed to fetch deferred encounter for MEV summary auto-open", error);
      }
    }

    if (!savedEncounter && deferredMevState.deferredEncounterSnapshot) {
      savedEncounter = deferredMevState.deferredEncounterSnapshot as PatientEncounter;
    }

    const shouldAutoOpen = shouldAutoOpenDeferredMevSummary({
      deferredReady: true,
      savedEncounter,
      configId,
      resumeExpectedEncounter: shouldResumeExpectedEncounter,
      config: mevSummaryAutoOpenConfig,
    });

    if (!shouldAutoOpen) return false;

    try {
      await generateMevSummarySectionPdf(savedEncounter as any, settings);
      setDeferredMevState((prev) => consumeDeferredMevOpen(prev));
      return true;
    } catch (error) {
      console.error("Failed to auto-open deferred MEV summary PDF", error);
      return false;
    }
  }, [
    configId,
    deferredMevState,
    methods,
    settings,
  ]);

  // Handle form submission
  const handleFormSubmit = async (data: any, temporaryActAsToken?: string) => {
    const submissionType = data.submissionType;
    const currentEditModeForSubmit = currentEditMode || data._isEditMode;
    const formEncounterId = data._encounterId;
    const shouldResumeExpectedEncounter = data._resumeExpectedEncounter === true;

    if (!temporaryActAsToken && hasModifiedSecurityField(data)) {
      const authorization = await requestAuthorization(SECURED_FIELD_AUTHORIZATION_REQUEST);
      if (!authorization) return;
      await handleFormSubmit(data, authorization.token);
      return;
    }

    setIsSubmitting(true);

    try {
      if (currentEditModeForSubmit && (encounter || formEncounterId)) {
        // Handle editing existing encounter
        const patientUpdatePayload = buildPatientPayload(data, submissionType);

        // Check if delegate was removed (had delegate before, but not now)
        const hadDelegateOriginally = originalValues?.hasDelegatePresent === true;
        const hasDelegateNow = data.hasDelegatePresent === true;
        const shouldClearDelegates = hadDelegateOriginally && !hasDelegateNow;
        const encounterUpdatePayload = buildEncounterSpecificPayload(data, {
          shouldClearDelegates
        });

        if (shouldResumeExpectedEncounter) {
          encounterUpdatePayload.status = "submitted";
          encounterUpdatePayload.arrived = moment().valueOf();
        }

        const fullPayload: Partial<PatientEncounter> = {
          ...encounterUpdatePayload,
          patient: patientUpdatePayload as any,
        };

        // Get visible fields from current form config to know which fields can be cleared
        const config = getFormConfig(configId);
        const visibleFields = extractVisibleFieldIds(config);

        // Filter to only include changed fields
        const encounterToUpdate = filterChangedFields(fullPayload, originalValues, data, visibleFields);


        // Only send update if there are actual changes
        if (Object.keys(encounterToUpdate).length === 0) {
          notify.success("No changes to save");
          setIsSubmitting(false);
          return;
        }

        const targetEncounterId = encounter?.id || formEncounterId;
        if (!targetEncounterId) {
          throw new Error("Encounter ID is missing");
        }
        const updatedEncounter = await updateEncounterWithOptionalActAs(targetEncounterId, encounterToUpdate, temporaryActAsToken);

        let savedEncounter = updatedEncounter as PatientEncounter | undefined;
        if (!savedEncounter) {
          try {
            savedEncounter = await encounterService.getOne(targetEncounterId);
          } catch (error) {
            console.warn("Failed to fetch saved encounter for MEV summary auto-open", error);
          }
        }
        if (savedEncounter) {
          if (canAttemptDeferredMevOpen(deferredMevState)) {
            await maybeAutoOpenDeferredMevSummary({
              encounterOverride: savedEncounter,
            });
          } else {
            void maybeAutoOpenMevSummary({
              mode: "update",
              savedEncounter,
              currentFormValues: data,
            });
          }
        }

        // Invalidate patient query to refresh patient data (for gpDetails, nextOfKin, previousNames)
        // Check if patient data was updated and get patient ID
        const patientId = encounter?.patient?.id || (typeof encounter?.patient === "string" ? encounter.patient : null);
        if (encounterToUpdate.patient && patientId) {
          global.queryClient.invalidateQueries(["patients", patientId]);
        }

        const name = formatName(data.firstName, data.lastName);
        notify.success(`Updated encounter for: ${name}`);
        setShouldConfirmClose(false); // Disable confirmation for successful update
        popModal();
      } else {
        // Handle creating new encounter
        const encounterPayloadFromHelper = buildEncounterSpecificPayload(data);

        // Determine source based on delegate presence
        const source = data.hasDelegatePresent
          ? "delegate"
          : "nurse";

        // Handle existing patient ID vs new patient data
        let patientData;
        if (data._existingPatientId) {
          // Use existing patient ID AND include patient data for updates
          // Backend will look up by ID and apply any changes
          const patientPayload = buildPatientPayload(data, submissionType);
          patientData = {
            id: data._existingPatientId,
            ...patientPayload,
          };
        } else {
          // Create new patient with provided data
          patientData = buildPatientPayload(data, submissionType);
        }

        const encounterToCreate: Partial<PatientEncounter> = {
          ...encounterPayloadFromHelper,
          source,
          patient: patientData as any,
        };

        const createdEncounter = await createEncounterWithOptionalActAs(encounterToCreate, temporaryActAsToken);

        void maybeAutoOpenMevSummary({
          mode: "create",
          savedEncounter: (createdEncounter as PatientEncounter) || null,
          currentFormValues: data,
        });
        if (submissionType === "anonymous") {
          notify.success("Anonymous patient created");
        } else {
          const name = formatName(data.firstName, data.lastName);
          notify.success(`Created patient: ${name}`);
        }
        setShouldConfirmClose(false); // Disable confirmation for successful create
        popModal();
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message ||
        (currentEditModeForSubmit ? "Failed to update encounter" : "Failed to create patient");
      notify.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const buildEncounterPayloadFromFormData = React.useCallback((formData: any): Partial<PatientEncounter> => {
    const submissionType = formData.submissionType;
    const patientPayload = buildPatientPayload(formData, submissionType);
    const encounterPayloadFromHelper = buildEncounterSpecificPayload(formData);
    return {
      ...encounterPayloadFromHelper,
      source: formData.hasDelegatePresent ? "delegate" : "nurse",
      patient: patientPayload as Patient,
    };
  }, []);

  const createEncounterAndOpenObsModal = async (formData: any, actAsToken?: string) => {
    const encounterPayload = buildEncounterPayloadFromFormData(formData);
    setIsOpeningObservation(true);
    try {
      const createdEncounter = await createEncounterWithOptionalActAs(encounterPayload, actAsToken);
      if (!createdEncounter) {
        throw new Error("Failed to create encounter");
      }
      activateInitialObservationDeferredFlow(createdEncounter as PatientEncounter, {
        openOnExitWithoutObservationSave: true,
      });
      loadEncounter(createdEncounter);
      notify.success("Patient created successfully");
      openObservationModal({
        mode: "create",
        encounter: createdEncounter as any,
        onObservationSaved: markInitialObservationSavedForDeferredOpen,
      });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to create patient";
      notify.error(errorMessage);
    } finally {
      setIsOpeningObservation(false);
    }
  };

  // Handler to open observation modal with patient data
  const handleOpenObservationModal = async () => {
    const isFormValid = await methods.trigger();
    if (!isFormValid) return;

    const formData = methods.getValues();
    const existingEncounterId = encounter?.id || formData._encounterId;

    if (existingEncounterId) {
      activateInitialObservationDeferredFlow({
        id: existingEncounterId,
        atsScore: Number(formData.atsScore),
      } as Pick<PatientEncounter, "id" | "atsScore">, {
        openOnExitWithoutObservationSave: false,
      });
      openObservationModal({
        mode: "create",
        encounter: {
          ...encounter,
          id: existingEncounterId,
          patient: {
            ...(encounter?.patient || {}),
            firstName: formData.firstName,
            lastName: formData.lastName,
            dateOfBirth: formData.dob,
          },
        } as any,
        onObservationSaved: markInitialObservationSavedForDeferredOpen,
      });
      return;
    }

    if (hasModifiedSecurityField(formData)) {
      const authorization = await requestAuthorization({
        ...SECURED_FIELD_AUTHORIZATION_REQUEST,
        message: "You changed a secured field. Confirm clinician and operator code to create the patient and add an initial observation.",
      });
      if (!authorization) return;
      await createEncounterAndOpenObsModal(formData, authorization.token);
      return;
    }

    await createEncounterAndOpenObsModal(formData);
  };

  // Determine modal title - check both initial mode and dynamic edit mode
  const modalTitle = configId === "concierge"
    ? (currentEditMode ? "Edit Patient" : "Create Patient")
    : (currentEditMode ? "Edit Patient" : "Triage");

  const closeModalWithDeferredOpen = React.useCallback(async () => {
    await maybeAutoOpenDeferredMevSummary({});
    popModal();
  }, [maybeAutoOpenDeferredMevSummary, popModal]);

  // Handle close with confirmation
  const handleClose = async () => {
    if (shouldConfirmClose && methods.formState.isDirty) {
      if (window.confirm("You have unsaved changes. Are you sure you want to close?")) {
        await closeModalWithDeferredOpen();
      }
    } else {
      await closeModalWithDeferredOpen();
    }
  };

  return (
    <>
      <Dialog fullScreen open onClose={handleClose}>
        <ActingAsBanner />
        <DialogTitle css={styles.container} sx={{ pb: 1 }}>
          <Text variant="h3">{modalTitle}</Text>

          <ValidationErrorBanner control={methods.control} />

          <Stack direction="row" gap={1}>
            <Button variant="outlined" onClick={handleClose}>Close</Button>
            {configId !== "concierge" && (
              <Button
                variant="contained"
                color="primary"
                disabled={hasObservations || isOpeningObservation}
                startIcon={isOpeningObservation ? <CircularProgress size={16} color="inherit" /> : undefined}
                onClick={handleOpenObservationModal}
              >
                {/* {isOpeningObservation ? "Opening..." : "Add Initial Observation"} */}
                Add Initial Observation
              </Button>
            )}
            <Button
              variant="contained"
              color="success"
              disabled={isSubmitting}
              onClick={() => {
                void handleSubmit((formData) => handleFormSubmit(formData))();
              }}
            >
              {currentEditMode ? "Update Patient" : "Add Patient"}
            </Button>
          </Stack>
        </DialogTitle>

        <GenericAddPatientFormBody
          methods={methods}
          normalizeTasks={normalizeTasks}
          settings={settings}
          currentEditMode={currentEditMode}
          setCurrentEditMode={setCurrentEditMode}
          temporaryDelegateData={temporaryDelegateData}
          setTemporaryDelegateData={setTemporaryDelegateData}
          onEncounterIdChange={handleEncounterIdChange}
          onOpenExternalRegisterDetails={openExternalRegisterDetails}
          configId={configId}
          encounter={encounter}
        />
      </Dialog>
      <AuthorizationDialog />
      <ExternalRegisterSecureDetailsModal
        open={detailsModalState.open}
        patientId={detailsModalState.patientId}
        alertId={detailsModalState.alertId}
        isConciergeMode={configId === "concierge"}
        onClose={closeExternalRegisterDetails}
      />
    </>
  );
};

// Modal wrapper component
export const GenericAddPatientModal: React.FC<ModalProps<GenericAddPatientModalProps>> = ({
  props: modalProps
}) => {
  return <GenericAddPatientModalContent {...modalProps} />;
};

// Hook to use the generic add patient modal
export const useGenericAddPatientModal = createUseModal(GenericAddPatientModal);
